import React, { useEffect, useState } from 'react'
import { Button, Dimmer, Form, Icon, Loader, Modal, Tab, TabPane } from 'semantic-ui-react'
import { ModalTextHeader, StyledMenuItem } from '../style'
import { useTranslation } from 'react-i18next'
import { CloudStorageService, FoldersType } from '~/api/services/cloudStorage'
import notify from '~/components/Notify'
import { handleErrors } from '~/utils/helpers'
import { useForm } from 'react-hook-form'
import { FormFields } from '~/components/FormFields'
import { Nullable } from '~/types'
import cloneDeep from 'lodash.clonedeep'

interface Props {
  openCreateFolderModal: boolean
  handleClose: (data?: FoldersType) => void
  editFolderId: Nullable<string>
  optionsData: any
}

const CreateEditFolder: React.FC<Props> = (props) => {
  const { openCreateFolderModal, handleClose, editFolderId, optionsData } = props
  const { t } = useTranslation()

  const [loading, setLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState<string | number>(0)

  const { handleSubmit, control, getValues, reset, formState, setValue } = useForm({
    mode: 'onSubmit'
  });

  useEffect(() => {
    if (openCreateFolderModal && !editFolderId) {
      parseFolderData()
    } else if (openCreateFolderModal && editFolderId) {
      fetchEditFolderData()
    }
  }, [openCreateFolderModal, editFolderId])

  function parseFolderData() {
    const languages = []
    optionsData?.languages?.map((language) => {
      languages.push({
        id: language.id,
        title: '',
        order: language.order
      })
    })
    languages?.sort((a, b) => a.order - b.order)
    reset({ visible: 0, languages })
  }

  async function fetchEditFolderData() {
    setLoading(true)
    try {
      const response = await CloudStorageService.getFolder(editFolderId)
      const languages = cloneDeep(response.data.languages)
      optionsData?.languages?.map((language) => {
        const indexLanguage = languages.findIndex(languageResponse => languageResponse.id === language.id)
        if (indexLanguage < 0) {
          languages.push({
            id: language.id,
            title: '',
            order: language.order,
            file: ''
          })
        } else {
          languages[indexLanguage].order = language.order
        }
      })
      languages?.sort((a, b) => a.order - b.order)
      response.data.languages = languages
      reset(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setLoading(false)
  }

  async function onSubmit(data) {
    setLoading(true);
    const editModal = Boolean(getValues('id'))
    try {
      if (editModal) {
        await CloudStorageService.updateFolder(data.id, data)
      } else {
        await CloudStorageService.createFolder({ ...data })
      }
      const folderResponse = await CloudStorageService.getCloudStorageFolders({ column: 'created_at' })
      handleClose(folderResponse?.data)
      if (data.id) {
        notify(t('Folder successfully updated'), 'success')
      } else {
        notify(t('Folder successfully created'), 'success')
      }
    } catch (error) {
      handleErrors(error)
    }
    setLoading(false);
  }

  function getMenuItem(pane, index) {
    const languagesErrors = formState.errors?.languages;
    const paneHasError = index in (languagesErrors || {});

    return <StyledMenuItem
      key={pane}
      className='tab-item'
    >
      <div className={`${paneHasError ? 'error' : ''}`}>{pane}</div>
    </StyledMenuItem>
  }

  function renderCreateFolderForm() {
    return <>
      <div style={{ marginLeft: 13 }}>
        <FormFields.Checkbox
          control={control}
          name='visible'
          defaultValue={0}
          checkboxValue={1}
          label={t('Visible')}
        />
        <FormFields.Checkbox
          control={control}
          name='visibleOnDashboard'
          defaultValue={0}
          checkboxValue={1}
          label={t('Visible on dashboard')}
        />
        <FormFields.Select
          control={control}
          name='farmerGroups'
          label={t('Farmer groups')}
          options={optionsData?.farmerGroups.map(farmerGroup => ({ value: farmerGroup.id, text: farmerGroup.name }))}
          placeholder={t('Farmer groups')}
          selectOnBlur={false}
          multiple
        />
      </div>
      <Tab
        menu={{ secondary: true, pointing: true }}
        activeIndex={activeIndex}
        renderActiveOnly={false}
        onTabChange={(_, data) => setActiveIndex(data.activeIndex)}
        panes={optionsData?.languages.map((language, index) => {
          return {
            menuItem: getMenuItem(language.label, index),
            pane: <TabPane
              active={index === activeIndex}
              style={{ border: 'none', marginBottom: 0 }}
              key={language.id}
            >
              <FormFields.Input
                control={control}
                label={t('Folder title')}
                placeholder={t('Folder title')}
                rules={{
                  required: {
                    value: true,
                    message: t('Please enter title')
                  }
                }}
                name={`languages[${index}].title`}
              />
            </TabPane>
          }
        })}
      />
    </>
  }

  function handleCloseModal() {
    setValue('farmerGroups', [])
    handleClose()
  }

  return <Modal
    open={openCreateFolderModal}
    onClose={() => handleCloseModal()}
    as={Form}
    onSubmit={handleSubmit(onSubmit)}
  >
    <Modal.Header style={{ display: 'flex', justifyContent: 'space-between' }}>
      <ModalTextHeader>{editFolderId ? t('Edit folder') : t('Create folder')}</ModalTextHeader>
      <Icon style={{ cursor: 'pointer' }} onClick={() => handleCloseModal()} name='close' size='large' />
    </Modal.Header>
    <Modal.Content style={{ position: 'relative' }}>
      <Dimmer inverted active={loading}>
        <Loader active={loading} />
      </Dimmer>
      {renderCreateFolderForm()}
    </Modal.Content>
    <Modal.Actions>
      <Button positive disabled={loading}>{editFolderId ? t('Update') : t('Create')}</Button>
      <Button onClick={() => handleCloseModal()} negative type='button'>{t('Close')}</Button>
    </Modal.Actions>
  </Modal>
}
export default CreateEditFolder